// M1 の API 型と呼び出し（05 §4.2/§4.3/§4.5/§4.6）。
//
// **暫定**：本来は OpenAPI からの生成型（05 §1.1）に置き換える。M1 では手書きだが、
// バックエンドの pydantic スキーマと 1:1 に保つ（ズレたら実行時に落ちる）。
//
// **職人向けと卸向けを分けて書く**のが要点＝返る形が違う（職人には定価・掛率・請求を返さない）。

import { api, printHtml } from "./index";

// ---------------------------------------------------------------------------
// 共通
// ---------------------------------------------------------------------------

/** 金額・数量は文字列で来る（NUMERIC の精度を JSON number で落とさないため）。 */
export type Money = string | null;

export type Paged<T> = { items: T[]; next_cursor: string | null };

// ---------------------------------------------------------------------------
// 職人（partner・05 §4.6）
// ---------------------------------------------------------------------------

export type ConnectedTenant = {
  tenant_id: number;
  tenant_name: string;
  tenant_status: "trial" | "active" | "suspended";
  connection_id: number;
  customer_id: number | null;
  connected_at: string;
};

export type PartnerProduct = {
  product_id: number;
  /** 商品番号（T-D79・D-95）。名前・規格・単位・単価まで同じ商品の**唯一の見分け**。未採番は null。 */
  display_no: string | null;
  name: string;
  spec: string | null;
  unit: string;
  /** 取引単価（単価3層の解決値）。**null は「単価未登録」＝金額を出さない**。 */
  unit_price: Money;
  /** 定価（D-101・M1.9）＝**掛率で解決した商品だけ**入る（内訳表示の打ち消し線側）。
   *  個別単価・定価そのままの商品は null＝画面は取引単価1行で出す。 */
  list_price: Money;
  /** 掛率（%・整数）。`list_price` と対＝掛率解決のときだけ。 */
  kake_percent: number | null;
  qty_start: number;
  qty_step: number;
  /** 分類（T-D79・D-95③）＝`products.category` そのもの。分類チップ・見出しの供給源で、
   *  カタログ名（`PartnerCatalog.name`）は職人に見せない。**null は未設定**＝「その他」に
   *  畳むのは画面の判断（サーバは分からない値を作らない）。 */
  category: string | null;
  /** 商品写真（FR-119・D-64）。未登録は null。 */
  image_url: string | null;
};

export type PartnerCatalog = {
  catalog_id: number;
  name: string;
  note: string | null;
  items: PartnerProduct[];
};

export type PartnerSite = { site_id: number; name: string; address: string | null };

/** 配送料の条件（FR-131・D-90）＝**卸ごと**。統合カート（A-10）のグループ表示に使う。
 *
 *  `mode="included"`（商品価格に込み）のときは**画面に何も出さない**＝「配送料 0円」と書くと
 *  別途かかる卸と見分けが付かず「無料キャンペーン」と読める。
 *  `free_threshold` があるときは「**あと△△円のご注文で配送料無料**」まで出す
 *  （あといくらかを出さないと閾値は判断材料にならない）。 */
export type DeliveryFee = {
  mode: "included" | "separate";
  /** 基本配送料（1注文あたり・円）。`mode="included"` では意味を持たない。 */
  base_fee: string;
  /** この金額以上で配送料無料。**null＝無料条件なし**（0 とは別＝0 は「常に無料」）。 */
  free_threshold: string | null;
  /** 「遠方は別途お見積り」等の補足（200字）。条件を増やさない代わりの逃げ道。 */
  note: string | null;
};

/** カートのグループ小計から、その卸のこの注文の配送料を出す（FR-131）。
 *
 *  ★**サーバ（`masters/pricing.resolve_delivery_fee`）と同じ規則**＝ここがズレると
 *  「カートに出た額」と「注文に残った額」が違う、という一番説明しにくい壊れ方をする。
 *  戻り値 null は「配送料という概念を使っていない卸」＝行そのものを出さない。 */
export function deliveryFeeFor(fee: DeliveryFee | undefined, itemsTotal: number): number | null {
  if (!fee || fee.mode !== "separate") return null;
  if (fee.free_threshold !== null && itemsTotal >= Number(fee.free_threshold)) return 0;
  return Number(fee.base_fee);
}

export type PartnerBin = {
  bin_id: number;
  name: string;
  cutoff_time: string;
  /** 06 §6.2：今日の受付が生きているか。false でも**翌営業日なら注文できる**。 */
  available_today: boolean;
  earliest_date: string;
};

/** 現場ごとの実行予算（材料費枠・税抜）。**取引先ごとに設定する**（D-73＝現場は卸のマスタ）。 */
export type SiteBudget = { site_label: string; amount: string };

export type PartnerOrderItem = {
  name: string;
  spec: string | null;
  /** A-08 のカテゴリ別内訳（FR-124）＝マスタの分類をそのまま返す。 */
  category: string | null;
  qty: Money;
  unit: string | null;
  unit_price: Money;
  amount: Money;
};

export type PartnerOrder = {
  order_id: number;
  display_no: string | null;
  tenant_id: number;
  tenant_name: string;
  /** 一括チェックアウト（D-83・FR-128）の親＝A-03 が親カードの下に子を並べる**結合キー**。
   *  単発注文（再注文・M1.6 前の注文）は null＝従来どおり単独カードになる。 */
  parent_checkout_session_id: number | null;
  /** 職人語彙（LC-08）＝受付済み/確定/出荷済み/配送中/納品済み・取消 */
  status: string;
  /** 5段バーのどこか（FR-104）。取消は -1＝並びの外。 */
  progress_index: number;
  progress_steps: string[];
  site_name: string | null;
  bin_name: string | null;
  desired_date: string | null;
  is_arriving_today: boolean;
  received_at: string;
  items: PartnerOrderItem[];
  /** **商品計**（明細の合計）＝配送料は含まない。1行でも単価未確定なら null。 */
  total_amount: Money;
  /** 受注時の配送料（FR-131）。**null＝この卸は「込み」＝画面に行を出さない**。 */
  delivery_fee: Money;
  timeline: { step: string; at: string }[];
};

/** 一括チェックアウト（FR-127・D-83）の1グループ＝**卸1社ぶんのカート**。 */
export type CheckoutGroupInput = {
  tenant_id: number;
  items: { product_id: number; qty: string }[];
  desired_date: string;
  bin_id: number;
  /** 現場は①アドレス帳（builder_site_id）②卸マスタ（site_id）③その他（site_other）の択一。 */
  builder_site_id?: number | null;
  site_id?: number | null;
  site_other?: string | null;
  note?: string | null;
};

/** 確定した子注文（卸ごと）。**冪等な再送では金額 2 項目が null**＝親は控えに金額を持たない（04 §2.2）。 */
export type CheckoutChild = {
  tenant_id: number;
  tenant_name: string;
  order_id: number;
  display_no: string | null;
  items_total: Money;
  delivery_fee: Money;
};

export type CheckoutResult = {
  checkout_session_id: number;
  /** 親の受付番号 `CS-YYMMDD-NN`（**職人側だけの概念**＝卸には出ない・D-85）。 */
  display_no: string;
  /** 全体合計＝**商品計＋配送料**。1グループでも金額未確定なら null。 */
  total_amount: Money;
  orders: CheckoutChild[];
  message: string;
};

/** 親注文（A-03 の親カード＝FR-128）。**子の実体は返らない**＝接続ごとの注文APIから取り画面で結合する。 */
export type CheckoutSession = {
  checkout_session_id: number;
  display_no: string;
  total_amount: Money;
  created_at: string;
  /** 子への参照の控え。**fan-in で取れなかった子＝取引が終了した卸**の検知に使う（黙って欠けさせない）。 */
  child_refs: { tenant_id: number; order_id: number; display_no: string | null }[];
};

/** 現場アドレス帳の1件（FR-129・D-86）＝**工務店グループで共有**する。 */
export type BuilderSite = {
  id: number;
  name: string;
  address: string | null;
  contact_person: string | null;
  phone: string | null;
  note: string | null;
  is_default: boolean;
  status: "active" | "archived";
};

export type BuilderSiteInput = {
  name: string;
  address?: string | null;
  contact_person?: string | null;
  phone?: string | null;
  note?: string | null;
  is_default?: boolean;
};

/** 工務店グループ（D-86）。**参加コードは本人たちにだけ見える**（卸・運営には出さない）。 */
export type BuilderCompany = {
  company_id: number;
  name: string;
  share_code: string;
  members: { builder_user_id: number; display_name: string | null; is_me: boolean }[];
};

export function partnerApi(bearer: string) {
  const base = (tenantId: number) => `/api/v1/partner/tenants/${tenantId}`;
  return {
    /** **tenant 無しで呼べる唯一のAPI**（03 §5）＝卸セレクタの中身。 */
    connections: () => api<ConnectedTenant[]>("/api/v1/partner/connections", { bearer }),
    redeem: (code: string) =>
      api<ConnectedTenant>("/api/v1/partner/connections", { method: "POST", body: { code }, bearer }),
    catalog: (t: number) => api<PartnerCatalog[]>(`${base(t)}/catalog`, { bearer }),
    sites: (t: number) => api<PartnerSite[]>(`${base(t)}/sites`, { bearer }),
    bins: (t: number) => api<PartnerBin[]>(`${base(t)}/bins`, { bearer }),
    /** 配送料の条件（FR-131・D-90）＝統合カタログと**同じ並列取得**で引く（卸ごとに条件が違う）。 */
    deliveryFee: (t: number) => api<DeliveryFee>(`${base(t)}/delivery-fee`, { bearer }),
    orders: (t: number, cursor?: string | null) =>
      api<Paged<PartnerOrder>>(`${base(t)}/orders${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`, {
        bearer,
      }),
    /** 子注文1件（A-03 の詳細ルート `/orders/{tenantId}/{orderId}`＝**通知ディープリンクの着地**・D-88）。 */
    order: (t: number, orderId: number) => api<PartnerOrder>(`${base(t)}/orders/${orderId}`, { bearer }),
    placeOrder: (t: number, body: unknown, idempotencyKey: string) =>
      api<{ order: PartnerOrder; message: string }>(`${base(t)}/orders`, {
        method: "POST",
        body,
        bearer,
        idempotencyKey,
      }),
    /** 同じ内容で再注文（FR-44改）＝現在のカタログ・単価で再解決したカート案。注文は作らない。 */
    reorder: (t: number, orderId: number) =>
      api<ReorderResult>(`${base(t)}/orders/${orderId}/reorder`, { method: "POST", bearer }),
    /** 職人プロフィール（FR-18・A-07）＝**卸ごとに独立**（D-63）。 */
    profile: (t: number) => api<BuilderProfile>(`${base(t)}/profile`, { bearer }),
    updateProfile: (t: number, body: BuilderProfileInput) =>
      api<BuilderProfile>(`${base(t)}/profile`, { method: "PUT", body, bearer }),
    /** 現場ごとの実行予算（FR-124・A-08・D-73）＝**取引先（卸）ごと × 現場名**。
     *  集計そのものは新 API を作らず、注文一覧をクライアントで合算する（D-71）。 */
    budgets: (t: number) => api<{ items: SiteBudget[] }>(`${base(t)}/budgets`, { bearer }),
    putBudget: (t: number, siteLabel: string, amount: string | null) =>
      api<{ items: SiteBudget[] }>(`${base(t)}/budgets`, {
        method: "PUT",
        body: { site_label: siteLabel, amount },
        bearer,
      }),

    // ── 一括チェックアウト（FR-127・D-83〜D-85）＝**tenant をパスに持たない**（親は
    //    どの卸のものでもない）。卸ごとの文脈はサーバが検証済みの接続にだけ張る（03 §5/§6.3）。
    /** 統合カートの確定。**全成功 or 全失敗**（部分成立を作らない）。
     *  `idempotencyKey` は**画面をマウントした時に1つ作って持ち回る**＝電波の悪い現場で
     *  もう一度押されても二重発注にならない。422 の `field_errors` は `groups[i].bin_id` の形で
     *  返るので、どの卸のカードのどの欄かを画面が指せる。 */
    checkout: (groups: CheckoutGroupInput[], idempotencyKey: string) =>
      api<CheckoutResult>("/api/v1/partner/checkout", {
        method: "POST",
        body: { groups },
        bearer,
        idempotencyKey,
      }),
    checkoutSessions: (cursor?: string | null) =>
      api<Paged<CheckoutSession>>(
        `/api/v1/partner/checkout-sessions${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
        { bearer },
      ),
    checkoutSession: (id: number) =>
      api<CheckoutSession>(`/api/v1/partner/checkout-sessions/${id}`, { bearer }),

    // ── 工務店グループと現場アドレス帳（FR-129・D-86）＝これも tenant を持たない
    //    （会社も現場も職人側のもので、どの卸のものでもない）。
    /** 自分の工務店グループ（無ければ**この呼び出しで作られる**＝名前は登録名から作る）。 */
    company: () => api<BuilderCompany>("/api/v1/partner/company", { bearer }),
    renameCompany: (name: string) =>
      api<BuilderCompany>("/api/v1/partner/company", { method: "PATCH", body: { name }, bearer }),
    /** 参加コードで合流。★**いま登録している現場は元のグループに残る**（移らない）＝
     *  画面は合流の**前**にそれを伝える（05 §6.2 A-07）。 */
    joinCompany: (shareCode: string) =>
      api<BuilderCompany>("/api/v1/partner/company/join", {
        method: "POST",
        body: { share_code: shareCode },
        bearer,
      }),
    /** 参加コードの再発行＝**旧コードは即失効**（漏れたときに締め直す口）。 */
    rotateShareCode: () =>
      api<BuilderCompany>("/api/v1/partner/company/rotate-code", { method: "POST", bearer }),
    leaveCompany: () => api<{ status: string }>("/api/v1/partner/company/leave", { method: "POST", bearer }),
    /** 除名（メンバー全員が実行できる＝管理者ロールを作らない）。**確認UIは必須**。 */
    removeCompanyMember: (builderUserId: number) =>
      api<BuilderCompany>("/api/v1/partner/company/remove-member", {
        method: "POST",
        body: { builder_user_id: builderUserId },
        bearer,
      }),
    builderSites: (includeArchived = false) =>
      api<BuilderSite[]>(
        `/api/v1/partner/builder-sites${includeArchived ? "?include_archived=true" : ""}`,
        { bearer },
      ),
    createBuilderSite: (body: BuilderSiteInput) =>
      api<BuilderSite>("/api/v1/partner/builder-sites", { method: "POST", body, bearer }),
    patchBuilderSite: (siteId: number, body: BuilderSiteInput & { status?: "active" | "archived" }) =>
      api<BuilderSite>(`/api/v1/partner/builder-sites/${siteId}`, { method: "PATCH", body, bearer }),

    // ── 保存済みセット（FR-132・D-100・A-11）＝これも tenant を持たない（会社の道具）。
    /** 一覧（明細込み）。**単価は返らない**＝適用時に現在のカタログへ再解決する（FR-44 と同じ規則）。 */
    savedSets: () => api<SavedSet[]>("/api/v1/partner/saved-sets", { bearer }),
    createSavedSet: (name: string, items: SavedSetItem[]) =>
      api<SavedSet>("/api/v1/partner/saved-sets", { method: "POST", body: { name, items }, bearer }),
    /** ハード DELETE（D-100⑤）＝**確認UIは必須**（復元の概念が無い）。 */
    deleteSavedSet: (savedSetId: number) =>
      api<{ status: string }>(`/api/v1/partner/saved-sets/${savedSetId}`, { method: "DELETE", bearer }),
  };
}

/** 保存済みセットの明細（FR-132）。qty は Decimal 文字列（注文の CartLine と同じ運び方）。 */
export type SavedSetItem = {
  tenant_id: number;
  product_id: number;
  qty: string;
};

export type SavedSet = {
  id: number;
  name: string;
  items: SavedSetItem[];
};

/** 職人プロフィール（backend BuilderProfileOut と 1:1）。**接続している卸ごとの値**。 */
export type BuilderProfile = {
  person_name: string | null;
  phone: string | null;
  address: string | null;
  updated_at: string;
};

export type BuilderProfileInput = {
  person_name: string | null;
  phone: string | null;
  address: string | null;
  updated_at: string; // 楽観ロック（規約10）
};

export type ReorderResult = {
  items: { product_id: number; name: string; spec: string | null; unit: string; unit_price: string | null; qty: string }[];
  unavailable: { name: string; reason: string }[];
};

// ---------------------------------------------------------------------------
// 卸コンソール（console・05 §4.2/§4.3/§4.5）
// ---------------------------------------------------------------------------

export type Customer = {
  id: number;
  display_no: string | null;
  name: string;
  person: string | null;
  fax_number: string | null;
  kake_rate: Money;
  payment_terms: string | null;
  /** 05 §6.1 C-09：列名は**「アプリ発注」**（D-39＝「LINE発注」から改称）。 */
  line_enabled: boolean;
  note: string | null;
  status: "active" | "disabled";
  updated_at: string;
};

/** 得意先の新規登録の入力（CustomerCreate と 1:1）。
 *  `display_no` を省く（null）と backend が採番する（`numbering.customer_display_no`）。
 *  金額・掛率は **文字列で送る**＝JS の number に入れると 0.1+0.2 の丸めが混ざる。 */
export type CustomerInput = {
  display_no: string | null;
  name: string;
  person: string | null;
  fax_number: string | null;
  kake_rate: string | null;
  payment_terms: string | null;
  line_enabled: boolean;
  note: string | null;
};

export type Site = {
  id: number;
  customer_id: number;
  name: string;
  address: string | null;
  status: string;
  updated_at: string;
};

export type Product = {
  id: number;
  display_no: string | null;
  name: string;
  spec: string | null;
  unit: string;
  list_price: Money;
  cost_price: Money;
  category: string | null;
  reorder_point: Money;
  status: "active" | "disabled";
  updated_at: string;
  /** 商品画像の署名URL（FR-119・D-64）。**15分で失効する**ので保存せず、表示のたびに取り直す。
   *  未登録は null＝画面はプレースホルダを出す。 */
  image_url: string | null;
};

/** 商品の新規登録の入力（ProductCreate と 1:1）。`display_no` 省略で採番。
 *  `unit` の値域は `masters/meta` の `units`（画面にハードコードしない＝FR-82）。 */
export type ProductInput = {
  display_no: string | null;
  name: string;
  spec: string | null;
  unit: string;
  list_price: string;
  cost_price: string | null;
  category: string | null;
  reorder_point: string | null;
};

/** 個別単価の1行（CustomerPriceOut と 1:1）。 */
export type CustomerPrice = { product_id: number; unit_price: string };

export type Order = {
  id: number;
  display_no: string | null;
  channel: "fax" | "mail" | "tel" | "web"; // line 除去（D-39・D-54⑥）＝backend models.Channel の鏡像
  connection_id: number | null;
  customer_id: number | null;
  site_id: number | null;
  desired_date: string | null;
  bin_id: number | null;
  note: string | null;
  /** 卸の業務語彙（LC-08 の左）＝未確認/確認済/出荷指示済/配送中/納品済/取消 */
  status_cache: string;
  received_at: string;
  updated_at: string;
  /** 受注時の配送料（FR-131・D-90）。**null＝この卸は「込み」＝画面に何も出さない**。
   *  合計（`total_amount`）は**商品計のまま**＝画面が「◯◯円（うち配送料 ◯◯円）」を組み立てる。 */
  delivery_fee: Money;
};

/** 配送料の設定（C-18 の「配送料の設定」・FR-131）。**卸 admin だけが変更できる**（staff は R）。
 *  `updated_at` が null＝**まだ一度も保存していない**（既定＝込み）。PUT にそのまま返すと
 *  「行が無いはず」の楽観ロックになる。 */
export type DeliveryFeeSetting = DeliveryFee & { updated_at: string | null };

export type DeliveryFeeInput = {
  mode: "included" | "separate";
  /** 金額は**文字列で送る**（JS の number に入れると丸めが混ざる＝単価と同じ扱い）。小数は 400。 */
  base_fee: string;
  free_threshold: string | null;
  note: string | null;
  updated_at: string | null;
};

export type OrderItem = {
  id: number;
  line_no: number;
  product_id: number | null;
  raw_text: string | null;
  qty: Money;
  unit: string | null;
  unit_price: Money;
  price_source: string | null;
  amount: Money;
};

export type ReviewFlag = {
  id: number;
  field: string;
  reason: string;
  // 04 §2.5・07 §6.2：候補ボタンは単一オブジェクト（{"type":"options","options":[...]} 等）＝backend の
  // ReviewFlagOut.candidates（dict）と一致させる（続123 レビュー #9＝SSoT ドリフト解消）。
  candidates: Record<string, unknown> | null;
  resolved_at: string | null;
  resolved_by: number | null;
};

export type OrderEvent = {
  id: number;
  type: string;
  actor_type: string;
  actor_id: number | null;
  meta: Record<string, unknown> | null;
  at: string;
};

/** 原文ビューア（FR-27）が画像を出すための添付参照＝backend AttachmentRefOut と 1:1。
 *  kind＝fax_image/photo（画像）・mail_attach（メール添付）。gcs_object は出さない（内部）＝
 *  画面は id で `GET /console/attachments/{id}/url`（署名URL・15分）を叩いて表示する。 */
export type AttachmentRef = { id: number; kind: string; content_type: string | null };

export type OrderDetail = {
  order: Order;
  items: OrderItem[];
  flags: ReviewFlag[];
  events: OrderEvent[];
  total_amount: Money;
  /** FR-30：**サーバの判定をそのまま使う**＝画面で「フラグ0件なら押せる」を書き直さない。 */
  can_approve: boolean;
  /** FR-103 改（D-54①）：承認済み以上・未取消・未納品なら「納品済みにする」を出せる。 */
  can_deliver: boolean;
  /** FR-33/FR-100 改（D-58）：進行操作の活性も**サーバの判定をそのまま使う**（状態機械を画面に二重実装しない）。 */
  can_ship: boolean;
  can_out_for_delivery: boolean;
  /** FR-27 原文ビューア＝AI/メール/FAX 起票の注文は取込原文（inbox）から起こされる。
   *  アプリ/電話直の注文は inbox を持たない＝null/空（原文は note・明細そのもの）。 */
  inbox_message_id: number | null;
  raw_text: string | null;
  attachments: AttachmentRef[];
  /** T-D25：誰からの注文か（一覧と同じ規則）。承認・出荷などの応答にも載る。 */
  orderer_name: string | null;
  orderer_name_is_registered: boolean;
  /** 職人が A-07 で登録した電話（D-63＝連絡先の卸側表示は T-D25）。**一覧には出さない**。 */
  orderer_phone: string | null;
  /** D-69⑧：この受注に紐づく**未完了の指示・依頼**の件数（取消の確認に出して依頼の取消へ誘導）。 */
  open_request_count: number;
  /** FR-130・D-89：LINE が届いていない可能性（通知が failed/skipped、または発注者がブロック中）。
   *  **判定はサーバが配る**（一覧 C-04 と詳細 C-03 で結論が食い違わない）。
   *  文言は「LINE が届いていない可能性があります（ブロック・送信失敗）。お電話等でご連絡ください」。 */
  line_alert: boolean;
  /** その注文に出した通知の履歴＝チップを出したときに「何が届いていないのか」を同じ画面で確かめる。 */
  notifications: OrderNotification[];
};

/** 通知1件（FR-130）。**宛先・本文は返らない**（規約13＝PII を持ち回らない）。 */
export type OrderNotification = {
  kind: string;
  status: "queued" | "sent" | "failed" | "skipped";
  sent_at: string | null;
  /** 失敗・スキップの分類（`no_destination` 等）。外部 API の生の応答は入らない。 */
  error: string | null;
};

/** 注文の手入力（backend StaffOrderRequest と 1:1）。`web` は職人アプリ専用＝ここには無い。 */
export type StaffOrderRequest = {
  customer_id: number;
  channel: "tel" | "fax" | "mail";
  site_id: number | null;
  desired_date: string | null;
  bin_id: number | null;
  note: string | null;
  /** 数量は**文字列で送る**（JS number の精度落ちを避ける＝Money/qty の受け取りと対称）。 */
  items: { product_id: number; qty: string }[];
};

/** FAX 手動アップロードの結果（backend IntakeResultOut）＝取込原文の id と添付 id。 */
export type IntakeResult = { inbox_message_id: number; attachment_ids: number[] };

export type InboxRow = {
  order: Order;
  customer_name: string | null;
  site_name: string | null;
  bin_name: string | null;
  item_count: number;
  open_flag_count: number;
  /** D-77：この受注に紐づく**未完了（open/in_progress）の指示・依頼**の件数。
   *  行のバッジに出す＝別カウント API は作らない（一覧の意味＝ひと目で掴む、を壊さない）。 */
  open_request_count: number;
  total_amount: Money;
  /** FR-103 改（D-54①）：受注一覧から1クリック納品＝**サーバの判定をそのまま使う**。 */
  can_deliver: boolean;
  /** FR-33/FR-100 改（D-58）：一覧からも出荷・配送を進められる。 */
  can_ship: boolean;
  can_out_for_delivery: boolean;
  /** T-D25：誰からの注文か。職人が登録した氏名（無ければ LINE 表示名）。
   *  **接続の無い注文（電話・FAX の手入力／AI 起票）は null**＝「いない」が正しい状態。 */
  orderer_name: string | null;
  /** その名前が**本人の登録名**か（false＝LINE の表示名＝本名とは限らない）。 */
  orderer_name_is_registered: boolean;
  /** FR-130・D-89：行に出す「LINE未達」チップ（詳細と同じ判定＝サーバが配る）。 */
  line_alert: boolean;
};

export type Dashboard = {
  unconfirmed: number;
  needs_review: number;
  due_today: number;
  by_channel: Record<string, number>;
  bins: { bin_id: number; name: string; cutoff_time: string; minutes_left: number | null; order_count: number }[];
};

export type Bin = {
  id: number;
  name: string;
  cutoff_time: string;
  sort_order: number;
  status: string;
  updated_at: string;
};

/** カタログ（FR-15）＝発注アプリの商品の並び。**価格は持たない**（products 参照＝二重持ち禁止）。 */
export type Catalog = {
  id: number;
  name: string;
  note: string | null;
  sort_order: number;
  status: "active" | "disabled";
  updated_at: string;
};

/** カタログ明細＝どの商品をどの順で、数量ステッパーの初期値/刻みをいくつで出すか。 */
export type CatalogItem = {
  id: number;
  product_id: number;
  sort_order: number;
  qty_start: number;
  qty_step: number;
};

export type InviteCode = {
  id: number;
  customer_id: number | null;
  code: string;
  expires_at: string | null;
  max_uses: number;
  used_count: number;
  revoked_at: string | null;
  created_at: string;
};

/** C-20 接続＝アプリ利用者1人（FR-07・D-67）。backend `ConnectionOut` と 1:1。
 *
 *  `person_name` は職人が A-07 で自分で登録した氏名（サーバで復号済み・03 §7 で卸は R）。
 *  未登録なら `display_name`（LINE 表示名）で代替する＝**行が無記名にならないこと**が眼目。
 *  `builder_status` は職人アカウント側の状態（`blocked`＝LINE をブロック中）で、
 *  接続の `status` とは別物。**両方出さないと画面が事実と食い違う**（06 §6 の revoke 連動が未実装）。 */
export type Connection = {
  id: number;
  builder_user_id: number;
  customer_id: number | null;
  status: "active" | "revoked";
  invite_code_id: number | null;
  connected_at: string;
  revoked_at: string | null;
  updated_at: string;
  person_name: string | null;
  display_name: string | null;
  builder_status: "unlinked" | "active" | "blocked" | null;
  customer_name: string | null;
};

export type ImportBatch = {
  id: number;
  kind: string;
  file_ref: string | null;
  status: "dry_run" | "applied" | "failed";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- 差分プレビューは行の形が kind ごとに違う
  summary: any;
  created_at: string;
};

/** C-12 スタッフ（FR-02）。**パスワードそのものは返らない**（設定済みかどうかだけ）。 */
export type Staff = {
  id: number;
  email: string;
  name: string;
  role: "admin" | "staff";
  status: "invited" | "active" | "disabled";
  has_password: boolean;
  created_at: string;
  updated_at: string;
  /** D-69：この人あての未完了の指示・依頼の件数（無効化の確認に出す）。 */
  open_request_count: number;
};

/** 招待・再発行の結果。`email_sent` ＝**実際に送れたか**（D-81④）。
 *  送信基盤が未設定でも、プロバイダに拒否されても false になる。画面はこれを見て
 *  「メールを送りました」と言い切るか「URL をお渡しください」に切り替える。 */
export type StaffInviteResult = {
  user: Staff;
  setup_path: string;
  expires_at: string;
  email_sent: boolean;
};

const C = "/api/v1/console";

export const console_ = {
  dashboard: () => api<Dashboard>(`${C}/dashboard`),
  inbox: (q = "") => api<Paged<InboxRow>>(`${C}/inbox${q}`),
  orders: (q = "") => api<Paged<InboxRow>>(`${C}/orders${q}`),
  /** 注文の手入力（C-17・FR-23）＝電話/FAX/メールで受けた注文を卸が起票する。
   *  `Idempotency-Key` は画面を開いた時に1つ作って持ち回る（二重クリックで2件作らない）。 */
  createOrder: (body: StaffOrderRequest, idempotencyKey: string) =>
    api<OrderDetail>(`${C}/orders`, { method: "POST", body, idempotencyKey }),
  order: (id: number) => api<OrderDetail>(`${C}/orders/${id}`),
  patchOrder: (id: number, body: unknown) =>
    api<OrderDetail>(`${C}/orders/${id}`, { method: "PATCH", body }),
  resolveFlag: (orderId: number, flagId: number, body: unknown) =>
    api<OrderDetail>(`${C}/orders/${orderId}/flags/${flagId}/resolve`, { method: "POST", body }),
  approve: (id: number, updatedAt: string) =>
    api<OrderDetail>(`${C}/orders/${id}/approve`, { method: "POST", body: { updated_at: updatedAt } }),
  /** 出荷した（FR-33 改・D-58）＝M1 は記録のみ・在庫は動かさない（M2）。 */
  ship: (id: number, updatedAt: string) =>
    api<OrderDetail>(`${C}/orders/${id}/ship`, { method: "POST", body: { updated_at: updatedAt } }),
  /** 配送に出た（FR-100 改・D-58）＝注文単位。便単位の一括は M2（FR-101）。 */
  outForDelivery: (id: number, updatedAt: string) =>
    api<OrderDetail>(`${C}/orders/${id}/out-for-delivery`, {
      method: "POST",
      body: { updated_at: updatedAt },
    }),
  deliver: (id: number, updatedAt: string) =>
    api<OrderDetail>(`${C}/orders/${id}/deliver`, { method: "POST", body: { updated_at: updatedAt } }),
  split: (id: number, updatedAt: string, lineNos: number[]) =>
    api<{ order: OrderDetail; new_order_id: number; new_display_no: string }>(`${C}/orders/${id}/split`, {
      method: "POST",
      body: { updated_at: updatedAt, line_nos: lineNos },
    }),
  reject: (id: number, updatedAt: string, reason: string) =>
    api<OrderDetail>(`${C}/orders/${id}/reject`, {
      method: "POST",
      body: { updated_at: updatedAt, reason },
    }),
  cancel: (id: number, updatedAt: string, reason: string) =>
    api<OrderDetail>(`${C}/orders/${id}/cancel`, {
      method: "POST",
      body: { updated_at: updatedAt, reason },
    }),

  customers: (q = "") => api<Paged<Customer>>(`${C}/customers${q}`),
  customer: (id: number) => api<Customer>(`${C}/customers/${id}`),
  /** 得意先の新規登録（C-09 単票・T-D8）。**admin のみ**（backend が ConsoleAdmin）。 */
  createCustomer: (body: CustomerInput) => api<Customer>(`${C}/customers`, { method: "POST", body }),
  patchCustomer: (id: number, body: unknown) =>
    api<Customer>(`${C}/customers/${id}`, { method: "PATCH", body }),
  /** 現場（FR-12）。`includeDisabled` は利用停止も含めて出す（管理画面用）。 */
  sites: (customerId: number, includeDisabled = false) =>
    api<Site[]>(`${C}/customers/${customerId}/sites${includeDisabled ? "?include_disabled=true" : ""}`),
  createSite: (customerId: number, body: { name: string; address: string | null }) =>
    api<Site>(`${C}/customers/${customerId}/sites`, { method: "POST", body }),
  patchSite: (siteId: number, body: Record<string, unknown>) =>
    api<Site>(`${C}/sites/${siteId}`, { method: "PATCH", body }),
  products: (q = "") => api<Paged<Product>>(`${C}/products${q}`),
  product: (id: number) => api<Product>(`${C}/products/${id}`),
  /** 商品の新規登録（C-10 単票・T-D8）。**admin のみ**（backend が ConsoleAdmin）。 */
  createProduct: (body: ProductInput) => api<Product>(`${C}/products`, { method: "POST", body }),
  /** 商品写真を外す（FR-119・T-D38）。すでに無くても成功する。 */
  deleteProductImage: (id: number) =>
    api<Product>(`${C}/products/${id}/image`, { method: "DELETE" }),
  patchProduct: (id: number, body: unknown) =>
    api<Product>(`${C}/products/${id}`, { method: "PATCH", body }),
  mastersMeta: () => api<{ units: string[]; categories: string[] }>(`${C}/masters/meta`),

  /** 個別単価＝単価3層の第3層（FR-14・04 §5）。**得意先スコープ**でしか引けない
   *  （API がそう作られている＝「この商品を安く出している得意先」の逆引きは持たない）。
   *  設定・変更は staff も可・**削除は admin のみ**（backend の認可に画面を合わせる）。 */
  customerPrices: (customerId: number) =>
    api<CustomerPrice[]>(`${C}/customers/${customerId}/prices`),
  putCustomerPrice: (customerId: number, body: { product_id: number; unit_price: string }) =>
    api<void>(`${C}/customers/${customerId}/prices`, { method: "PUT", body }),
  deleteCustomerPrice: (customerId: number, productId: number) =>
    api<void>(`${C}/customers/${customerId}/prices/${productId}`, { method: "DELETE" }),
  /** 便（C-18・FR-13）。`include_disabled` は利用停止も含めて出す（管理画面用）。 */
  bins: (includeDisabled = false) =>
    api<Bin[]>(`${C}/bins${includeDisabled ? "?include_disabled=true" : ""}`),
  createBin: (body: { name: string; cutoff_time: string; sort_order: number }) =>
    api<Bin>(`${C}/bins`, { method: "POST", body }),
  patchBin: (id: number, body: Record<string, unknown>) =>
    api<Bin>(`${C}/bins/${id}`, { method: "PATCH", body }),

  /** 配送料の設定（C-18 の「配送料の設定」セクション・FR-131・D-90）。
   *  **未設定でも既定値（込み）が返る**＝画面が「在る／無い」で分岐しない。
   *  保存は admin のみ・`updated_at`（null 可）で楽観ロック。 */
  deliveryFee: () => api<DeliveryFeeSetting>(`${C}/delivery-fee`),
  putDeliveryFee: (body: DeliveryFeeInput) =>
    api<DeliveryFeeSetting>(`${C}/delivery-fee`, { method: "PUT", body }),

  /** スタッフと権限（C-12・FR-02/FR-115・T-D23）。
   *  設定URLは**この1回しか出ない**。`send_email` でメールも送る（D-81④＝**両方やる**）。 */
  staff: () => api<Staff[]>(`${C}/users`),
  inviteStaff: (body: { email: string; name: string; role: "admin" | "staff"; send_email: boolean }) =>
    api<StaffInviteResult>(`${C}/users`, { method: "POST", body }),
  patchStaff: (id: number, body: { role?: "admin" | "staff"; status?: "active" | "disabled"; updated_at: string }) =>
    api<Staff>(`${C}/users/${id}`, { method: "PATCH", body }),
  /** 設定URLの再発行（FR-112②・03 §4.1 の②管理経路＝**M1 の唯一の再設定経路**）。
   *  **古い未使用URLはここで無効になる**。応答の URL は1回だけ現れる。 */
  reissueStaffSetupLink: (id: number, sendEmail: boolean) =>
    api<StaffInviteResult>(`${C}/users/${id}/reset-link`, {
      method: "POST",
      body: { send_email: sendEmail },
    }),

  /** 帳票の発行（FR-45/46・D-76・M1.5）＝**印刷用 HTML を POST で取り、新規タブで開く**。
   *  保存はしない（都度生成）。番号は受付番号から決定的に導くので再印刷でも同じ。
   *  ★`printHtml` を使う＝`window.open(url)` にすると GET になり CSRF の前提が崩れる。 */
  printDeliveryNote: (orderId: number) => printHtml(`${C}/orders/${orderId}/delivery-note`),
  printPickingList: (binId: number, date: string) =>
    printHtml(`${C}/picking-list`, { bin_id: binId, date }),

  /** カタログ（C-19・FR-15）。**戻り型は更新に必要な updated_at まで含む**（楽観ロック）。 */
  catalogs: (includeDisabled = false) =>
    api<Catalog[]>(`${C}/catalogs${includeDisabled ? "?include_disabled=true" : ""}`),
  createCatalog: (body: { name: string; note?: string | null; sort_order: number }) =>
    api<Catalog>(`${C}/catalogs`, { method: "POST", body }),
  patchCatalog: (id: number, body: Record<string, unknown>) =>
    api<Catalog>(`${C}/catalogs/${id}`, { method: "PATCH", body }),
  catalogItems: (catalogId: number) => api<CatalogItem[]>(`${C}/catalogs/${catalogId}/items`),
  /** **真の UPSERT**＝同じ product_id を送り直すと既存行の並び・数量刻みを更新する。 */
  upsertCatalogItem: (
    catalogId: number,
    body: { product_id: number; sort_order: number; qty_start: number; qty_step: number },
  ) => api<CatalogItem>(`${C}/catalogs/${catalogId}/items`, { method: "POST", body }),
  deleteCatalogItem: (catalogId: number, itemId: number) =>
    api<void>(`${C}/catalogs/${catalogId}/items/${itemId}`, { method: "DELETE" }),

  /** C-20 接続（FR-07・T-D24）。**参照は staff も可・変更と解除は admin のみ**（03 §7）。
   *  ページングは持たない＝`CONNECTION_LIST_LIMIT`（500）で頭打ちになる。画面はその旨を出す。 */
  connections: (includeRevoked = false, customerId?: number) => {
    const q = new URLSearchParams();
    if (includeRevoked) q.set("include_revoked", "true");
    // D-79：得意先の絞り込みは**サーバ側**（クライアント filter だと上限500件で取りこぼす）
    if (customerId != null) q.set("customer_id", String(customerId));
    const qs = q.toString();
    return api<Connection[]>(`${C}/connections${qs ? `?${qs}` : ""}`);
  },
  /** 得意先の付け替え。**`updated_at` 必須**（楽観ロック＝規約10）・解除済みは 422。 */
  reassignConnection: (id: number, customerId: number, updatedAt: string) =>
    api<Connection>(`${C}/connections/${id}`, {
      method: "PATCH",
      body: { customer_id: customerId, updated_at: updatedAt },
    }),
  /** 接続の解除（理由必須＝FR-80）。**`updated_at` は要求しない**（D-67⑤）＝相手が
   *  連絡先を直しただけで取引を止められない、を避ける。2回目は 422。 */
  revokeConnection: (id: number, reason: string) =>
    api<Connection>(`${C}/connections/${id}/revoke`, { method: "POST", body: { reason } }),

  // 指示・依頼（C-21・T-D40）
  requests: (qs = "") => api<WorkRequestList>(`${C}/requests${qs}`),
  createRequest: (body: {
    order_id?: number | null;
    kind: "procure" | "ship" | "other";
    assignee_type: "staff" | "operator";
    assignee_user_id?: number | null;
    body: string;
  }) => api<WorkRequest>(`${C}/requests`, { method: "POST", body }),
  /** 状態更新・完了メモ・完了確認。**`updated_at` 必須**（楽観ロック＝規約10）。 */
  patchRequest: (
    id: number,
    body: { updated_at: string; status?: WorkRequest["status"]; reply_note?: string; done_ack?: boolean },
  ) => api<WorkRequest>(`${C}/requests/${id}`, { method: "PATCH", body }),

  inviteCodes: () => api<InviteCode[]>(`${C}/invite-codes`),
  issueInviteCode: (customerId: number, maxUses?: number) =>
    api<InviteCode>(`${C}/customers/${customerId}/invite-codes`, {
      method: "POST",
      body: maxUses ? { max_uses: maxUses } : {},
    }),
  revokeInviteCode: (id: number) =>
    api<InviteCode>(`${C}/invite-codes/${id}/revoke`, { method: "POST" }),

  imports: () => api<ImportBatch[]>(`${C}/imports`),
  applyImport: (id: number) => api<ImportBatch>(`${C}/imports/${id}/apply`, { method: "POST" }),

  /** FR-27 原文ビューア＝添付の署名URL（15分・直接公開しない）。他テナント・不在は 404。
   *  署名URLは失効するので**都度取得**する（画面は表示のたびにここを叩く）。 */
  attachmentUrl: (attachmentId: number) =>
    api<{ url: string }>(`${C}/attachments/${attachmentId}/url`),
};

/** FAX（複合機スキャン→アップロード）を受注箱の原文として取り込む（FR-20〜22・S9b）。
 *  CSV 投入と同じく multipart なので `api()`（JSON 前提）を通さず fetch を直接組む。 */
export async function uploadFax(file: File): Promise<IntakeResult> {
  const form = new FormData();
  form.append("file", file);
  const { config } = await import("@kenzai/config");
  const res = await fetch(`${config.apiBase}${C}/inbox/fax`, {
    method: "POST",
    headers: { [config.csrfHeader]: "1" },
    credentials: "include",
    body: form,
  });
  const json = await res.json();
  if (!res.ok) {
    const { ApiError } = await import("./index");
    throw new ApiError(
      json?.error?.code ?? "internal",
      json?.error?.message ?? "取り込みに失敗しました。",
      res.status,
      json?.error?.request_id,
      json?.error?.field_errors ?? [],
    );
  }
  return json as IntakeResult;
}

// ---------------------------------------------------------------------------
// 運営ポータル（ops・05 §4.7/08 §5）＝**ops_admin セッションCookie**で叩く。
//
// backend app/domains/ops/schemas.py と 1:1（ズレたら実行時に落ちる）。
// status は backend が `str`（値域は trial/active/suspended）＝narrower に嘘をつかない。
// created_at・expires_at は datetime → JSON では ISO 文字列。
// ---------------------------------------------------------------------------

/** 一覧の1行（TenantOut）。 */
export type OpsTenant = {
  id: number;
  code: string;
  name: string;
  status: string;
  plan: string | null;
  is_demo: boolean;
  created_at: string;
};

/** 詳細（TenantDetailOut）＝一覧＋スタッフ数・接続数。 */
export type OpsTenantDetail = OpsTenant & {
  staff_count: number;
  connection_count: number;
};

/** 開設結果（OpenTenantResponse）。**setup_path の平文はここにしか無い**（DB は SHA-256）。 */
export type OpenTenantResult = {
  tenant: OpsTenantDetail;
  setup_path: string;
  expires_at: string;
};

/** 卸のスタッフ1人（TenantUserOut・08 §5.1）。 */
export type OpsTenantUser = {
  id: number;
  email: string;
  name: string | null;
  role: string;
  status: string;
  locked_until: string | null;
  last_login_at: string | null;
};

/** 再発行の結果（ReissueTokenResponse）。**setup_path の平文はここにしか無い**（1回だけ表示）。 */
export type ReissueSetupLinkResult = {
  user: OpsTenantUser;
  setup_path: string;
  expires_at: string;
  purpose: string;
};

/** ①概況（OverviewOut・FR-70）＝クロステナントの集計値のみ（PII/原文は返らない）。 */
export type OpsOverview = {
  tenants_total: number;
  tenants_trial: number;
  tenants_active: number;
  tenants_suspended: number;
  orders_today: number;
  unresolved_orders: number;
  ai_failed_orders: number;
  stalled_orders: number;
  generated_at: string;
};

/** ②利用状況（UsageOut・FR-72）＝テナント別 日次注文数（`counts` は `days` と同じ並び・0 埋め）。 */
export type OpsUsageTenant = {
  tenant_id: number;
  code: string;
  name: string;
  counts: number[];
  total: number;
};
export type OpsUsage = { days: string[]; tenants: OpsUsageTenant[] };

/** ③監査ログ（AuditLogOut/AuditSearchOut・FR-71）＝誰がいつ何を＋keyset ページング。 */
export type OpsAuditLog = {
  id: number;
  at: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  reason: string | null;
  actor_email: string | null;
  actor_name: string | null;
};
export type OpsAuditSearch = { items: OpsAuditLog[]; next_cursor: string | null };

/** undefined/null を落とすクエリ文字列（`?a=1&b=2` or `""`）。 */
function opsQs(p: Record<string, string | number | undefined | null>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) if (v !== undefined && v !== null) u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : "";
}

/** 指示・依頼（C-21・FR-121/122・D-69）。staff 宛＝社内指示／operator 宛＝運営への発注代行依頼。 */
export type WorkRequest = {
  id: number;
  order_id: number | null;
  order_display_no: string | null;
  order_status: string | null;
  customer_name: string | null;
  desired_date: string | null;
  kind: "procure" | "ship" | "other";
  assignee_type: "staff" | "operator";
  assignee_user_id: number | null;
  assignee_name: string | null;
  created_by_id: number | null;
  created_by_name: string | null;
  body: string;
  reply_note: string | null;
  status: "open" | "in_progress" | "done" | "cancelled";
  done_ack_at: string | null;
  created_at: string;
  updated_at: string;
};

export type WorkRequestList = {
  items: WorkRequest[];
  next_cursor: string | null;
  /** Shell のバッジ＝自分宛の未完了＋自分が出して未確認の完了（D-69⑥）。 */
  badge_count: number;
};

/** 運営の依頼キューの1行（08 §10）。**発注者（職人）の情報は含まれない**（D-63）。 */
export type OpsWorkRequest = {
  id: number;
  tenant_id: number;
  tenant_name: string;
  order_id: number | null;
  order_display_no: string | null;
  order_status: string | null;
  customer_name: string | null;
  desired_date: string | null;
  kind: "procure" | "ship" | "other";
  body: string;
  reply_note: string | null;
  status: "open" | "in_progress" | "done" | "cancelled";
  created_at: string;
  updated_at: string;
};

export const ops = {
  tenants: () => api<OpsTenant[]>(`/api/v1/ops/tenants`),
  tenant: (id: number) => api<OpsTenantDetail>(`/api/v1/ops/tenants/${id}`),
  openTenant: (body: { name: string; plan?: string | null; admin_email: string; admin_name: string }) =>
    api<OpenTenantResult>(`/api/v1/ops/tenants`, { method: "POST", body }),
  activate: (id: number) =>
    api<OpsTenantDetail>(`/api/v1/ops/tenants/${id}/activate`, { method: "POST" }),
  suspend: (id: number, reason: string) =>
    api<OpsTenantDetail>(`/api/v1/ops/tenants/${id}/suspend`, { method: "POST", body: { reason } }),
  resume: (id: number) => api<OpsTenantDetail>(`/api/v1/ops/tenants/${id}/resume`, { method: "POST" }),
  /** テナントのスタッフ一覧（08 §5.1）＝再発行の対象を選ぶために要る。 */
  tenantUsers: (id: number) => api<{ items: OpsTenantUser[] }>(`/api/v1/ops/tenants/${id}/users`),
  /** パスワード設定URLの再発行（FR-112②）。**URL は1回だけ返る**（DB は SHA-256）。 */
  reissueSetupLink: (tenantId: number, userId: number, reason: string) =>
    api<ReissueSetupLinkResult>(`/api/v1/ops/tenants/${tenantId}/users/${userId}/reset-link`, {
      method: "POST",
      body: { reason },
    }),
  // ①監視・②利用状況・③監査（08 §5.1）
  overview: () => api<OpsOverview>(`/api/v1/ops/overview`),
  usage: (params: { days?: number; tenant_id?: number } = {}) =>
    api<OpsUsage>(`/api/v1/ops/usage${opsQs(params)}`),
  auditSearch: (params: { target_id?: string; action?: string; limit?: number; cursor?: string } = {}) =>
    api<OpsAuditSearch>(`/api/v1/ops/audit${opsQs(params)}`),

  // 発注代行キュー（08 §10・T-D40）＝**operator 宛のみ**が返る（API＋RLS の二重）
  /** `status` は**複数指定できる**（`?status=open&status=in_progress`）＝「未完了だけ」を
   *  1回の問い合わせで出す。★open だけに絞ると、受領した瞬間に一覧から消えて
   *  **完了を記録できなくなる**（実際に E2E が捕まえた）。 */
  requests: (params: { status?: string[]; limit?: number; cursor?: string } = {}) => {
    const u = new URLSearchParams();
    for (const s of params.status ?? []) u.append("status", s);
    if (params.limit) u.set("limit", String(params.limit));
    if (params.cursor) u.set("cursor", params.cursor);
    const qs = u.toString();
    return api<{ items: OpsWorkRequest[]; next_cursor: string | null }>(
      `/api/v1/ops/requests${qs ? `?${qs}` : ""}`,
    );
  },
  patchRequest: (id: number, body: { updated_at: string; status: "in_progress" | "done"; reply_note?: string }) =>
    api<OpsWorkRequest>(`/api/v1/ops/requests/${id}`, { method: "PATCH", body }),
};

/** 商品写真の登録・差し替え（FR-119・T-D38）。multipart なので `api()` を通さない。
 *  **形式の判定は backend が中身（先頭バイト）で行う**＝ここで拡張子を見て安心しない。 */
export async function uploadProductImage(productId: number, file: File): Promise<Product> {
  const form = new FormData();
  form.append("file", file);
  const { config } = await import("@kenzai/config");
  const res = await fetch(`${config.apiBase}${C}/products/${productId}/image`, {
    method: "PUT",
    headers: { [config.csrfHeader]: "1" },
    credentials: "include",
    body: form,
  });
  const json = await res.json();
  if (!res.ok) {
    const { ApiError } = await import("./index");
    throw new ApiError(
      json?.error?.code ?? "internal",
      json?.error?.message ?? "写真を登録できませんでした。",
      res.status,
      json?.error?.request_id,
      json?.error?.field_errors ?? [],
    );
  }
  return json as Product;
}

/** CSV 投入だけは multipart なので `api()` を通さない（JSON 前提のため）。 */
export async function dryRunImport(kind: string, file: File): Promise<ImportBatch> {
  const form = new FormData();
  form.append("kind", kind);
  form.append("file", file);
  const { config } = await import("@kenzai/config");
  const res = await fetch(`${config.apiBase}${C}/imports`, {
    method: "POST",
    headers: { [config.csrfHeader]: "1" },
    credentials: "include",
    body: form,
  });
  const json = await res.json();
  if (!res.ok) {
    const { ApiError } = await import("./index");
    throw new ApiError(
      json?.error?.code ?? "internal",
      json?.error?.message ?? "取り込みに失敗しました。",
      res.status,
      json?.error?.request_id,
      json?.error?.field_errors ?? [],
    );
  }
  return json as ImportBatch;
}
